import React, { useMemo } from "react";
import { AnalysisResult } from "../../contracts/scoring";
import { PerformanceCompare } from "../../components/PerformanceCompare";
import { getTimingToleranceMs } from "../../services/severityEngine";
import { AdviceCard } from "../../components/SmartTrainer/AdviceCard";

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return "—";
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

const isLikelyVideoFile = (name?: string) => {
  const n = (name || "").toLowerCase().trim();
  return (
    n.endsWith(".mp4") ||
    n.endsWith(".mov") ||
    n.endsWith(".webm") ||
    n.endsWith(".m4v") ||
    n.endsWith(".mkv")
  );
};

// Fallback: si tu n’as pas filename, on peut tout de même tenter une vidéo,
// mais seulement si on a un blob URL (souvent issu MediaRecorder)
// ou si tu sais que ton pipeline upload en conteneur vidéo.
const isProbablyBlobUrl = (url?: string) => !!url && url.startsWith("blob:");

export const RhythmTab: React.FC<{
  result: AnalysisResult;
  lang: "en" | "fr";
  isFullscreen?: boolean;
}> = ({ result, lang, isFullscreen = false }) => {
  const metrics = result.session.metrics;
  const levelId = result.coaching_level || 4;
  const bpm = result.session.bpm || 90;
  const isFr = lang === "fr";

  const score = metrics.rhythm || 0;
  const avgError = Math.round(metrics.timingErrorMs || 0);
  const tolerance = getTimingToleranceMs(levelId, bpm);
  const diagnostics = result.feedback.diagnostics.filter((d) => d.domain === "TIMING");

  const verdict = useMemo(() => {
    const devs = result.session.metrics.debug?.timingDeviationMs || [];
    const mean = devs.reduce((a: number, b: number) => a + b, 0) / (devs.length || 1);

    if (score > 85) return isFr ? "Parfaitement Calé" : "Perfectly Locked";
    return mean < 0
      ? isFr
        ? "Tendance : Rushing"
        : "Trend: Rushing"
      : isFr
      ? "Tendance : Dragging"
      : "Trend: Dragging";
  }, [score, result.session.metrics.debug, isFr]);

  const measuredCards: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [
      { label: "Timing score", value: `${fmt(score)}%`, hint: "Plus haut = mieux" },
      { label: "Avg error", value: `${fmt(avgError)} ms`, hint: "Plus bas = mieux" },
      { label: `Tolerance (L${levelId})`, value: `±${fmt(tolerance)} ms`, hint: "Seuil de validation" },
      { label: "Trend", value: verdict, hint: "Direction moyenne" },
      { label: "BPM", value: String(bpm), hint: "Tempo analysé" },
      { label: "Status", value: score > 70 ? "VALIDÉ" : "À REVOIR", hint: "Décision" },
    ];
    return cards.slice(0, 6);
  }, [score, avgError, tolerance, levelId, verdict, bpm]);

  const refUrl = result.alignedUrls?.ref || "";
  const stuUrl = result.alignedUrls?.stu || "";

  // ✅ Match = audio + video (si le média uploadé est un container vidéo)
  const refFile = (result.session as any)?.truth_report?.tech_pack?.filenames?.ref as string | undefined;
  const stuFile = (result.session as any)?.truth_report?.tech_pack?.filenames?.stu as string | undefined;

  // 1) filename-based
  const filenameSaysRefVideo = isLikelyVideoFile(refFile);
  const filenameSaysStuVideo = isLikelyVideoFile(stuFile);

  // 2) fallback: si blob URL, on tente aussi la vidéo (souvent MediaRecorder => webm/mp4)
  const fallbackRefVideo = isProbablyBlobUrl(refUrl);
  const fallbackStuVideo = isProbablyBlobUrl(stuUrl);

  const refVideoUrl = filenameSaysRefVideo || fallbackRefVideo ? refUrl : undefined;
  const stuVideoUrl = filenameSaysStuVideo || fallbackStuVideo ? stuUrl : undefined;

  const refEnv = result.rhythm?.refEnvelope || [];
  const stuEnv = result.rhythm?.stuEnvelope || [];

  return (
    <div className={`flex flex-col gap-10 animate-fade-in ${isFullscreen ? "h-full p-6" : "pb-12"}`}>
      {!isFullscreen && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-smart-accent to-transparent"></div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">
                Precision Match
              </span>

              <div className="flex items-baseline gap-2">
                <span
                  className={`text-8xl font-black italic tracking-tighter ${
                    score > 80 ? "text-emerald-400" : "text-smart-accent"
                  }`}
                >
                  {score}
                </span>
                <span className="text-xl font-bold text-slate-700">/100</span>
              </div>

              <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                {verdict}
              </div>
            </div>

            <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-smart-accent/20 flex items-center justify-center text-2xl">
                  ⏱️
                </div>
                <h4 className="text-sm font-black text-white uppercase italic tracking-widest">
                  Pourquoi ce score ?
                </h4>
              </div>

              <p className="text-lg text-slate-300 italic leading-relaxed pl-4 border-l-2 border-smart-accent/40">
                "Le score de rythme mesure votre précision d’alignement sur le tempo : l’erreur moyenne (en ms) est
                comparée à la tolérance de votre niveau (L{levelId})."
              </p>

              <div className="space-y-3">
                {diagnostics.length > 0 ? (
                  diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
                ) : (
                  <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl text-center">
                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">
                      Timing validé
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {measuredCards.map((m, i) => (
                <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
                  <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
                  {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ✅ Match module: + haut + confortable, et les vidéos seront en "YouTube-like" dans PerformanceCompare */}
      <div className={`${isFullscreen ? "h-full w-full" : "h-[720px]"}`}>
        <PerformanceCompare
          refUrl={refUrl}
          stuUrl={stuUrl}
          refEnvelope={refEnv}
          stuEnvelope={stuEnv}
          duration={result.session.duration}
          lagMs={result.session.lagMs}
          bpm={bpm}
          lang={lang}
          refVideoUrl={refVideoUrl}
          stuVideoUrl={stuVideoUrl}
        />
      </div>
    </div>
  );
};